import React from "react";
import { PropTypes } from "prop-types";
import { Typography, Tab, Tabs as MuiTabs, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { withSize } from "react-sizeme";
import { Tooltip } from "../Tooltip";

const useStyles = makeStyles(theme => ({
  root: ({ disableHighlight }) => ({
    minHeight: disableHighlight
      ? 58
      : theme.components.MuiTabs.styleOverrides.flexContainer.height
  }),
  flexContainer: ({ disableHighlight }) => ({
    ...(disableHighlight && { height: 58 })
  }),
  selected: {
    color: theme.palette.primary.main
  },
  tab: {
    "&.Mui-selected": {
      backgroundColor: ({ disableHighlight }) =>
        disableHighlight ? "transparent" : theme.palette.action.hover
    },
    pointerEvents: "auto !important" // override mui disabled pointer events style
  },
  notSelected: {
    color: theme.palette.text.secondary
  }
}));

function Tabs({
  tabs,
  currentTab,
  onChange,
  variant,
  textVariant,
  centered,
  disableHighlight
}) {
  const classes = useStyles({ disableHighlight });
  const theme = useTheme();

  const tabsVariant = useMediaQuery(theme.breakpoints.down("sm"))
    ? "fullWidth"
    : variant;

  const tabList = tabs.map(tab =>
    typeof tab === "string" ? { label: tab } : tab
  );

  return (
    <MuiTabs
      value={currentTab}
      onChange={(e, newTab) => onChange(newTab)}
      indicatorColor="primary"
      textColor="primary"
      variant={tabsVariant}
      centered={centered}
      scrollButtons={false}
      classes={{ flexContainer: classes.flexContainer, root: classes.root }}
    >
      {tabList.map((tab, index) => (
        <Tab
          key={index}
          classes={{
            selected: classes.selected,
            root: classes.tab,
            textColorPrimary: classes.notSelected
          }}
          disabled={tab.disabled}
          label={
            <Tooltip title={tab.tooltip} placement="bottom-start">
              <Typography variant={textVariant} noWrap>
                {tab.label}
              </Typography>
            </Tooltip>
          }
        />
      ))}
    </MuiTabs>
  );
}

export default withSize()(Tabs);

Tabs.propTypes = {
  textVariant: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({ label: PropTypes.string, disabled: PropTypes.bool }),
      PropTypes.string
    ])
  )
};

Tabs.defaultProps = {
  textVariant: "h5",
  variant: "standard"
};
